import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink, ImageOff, X } from "lucide-react";
import { KIND_META, CONFIDENCE_META, kindMetaBadges } from "./efConsts";

/**
 * InlineDetailPanel — persistent right-column subject panel on desktop.
 *
 * Always renders a subject (the anchor by default). Lists categories,
 * connection summary, source URLs, and a primary CTA to open the full
 * entity detail page.
 */
export const InlineDetailPanel = ({
  entity,
  allNodes = [],
  edges = [],
  sources = [],
  onClear = null,
}) => {
  if (!entity) return null;
  const meta = KIND_META[entity.kind] || {};
  const cm = CONFIDENCE_META[entity.confidence_level] || {};

  // Connection count derived from edges + this entity's id
  const conn = edges.filter(
    (e) => e.src_entity_id === entity.id || e.dst_entity_id === entity.id,
  ).length;

  // Sources referenced by this entity directly
  const refIds = new Set(entity.source_refs || []);
  const ownSources = sources.filter((s) => refIds.has(s.id));

  return (
    <aside
      data-testid="ef-inline-detail-panel"
      className="border border-[#2D2A26] bg-[#0F0F0F] flex flex-col h-full overflow-hidden"
    >
      <div className="px-3 py-2 border-b border-[#2D2A26] flex items-center justify-between gap-2">
        <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#D92D20]">
          // SELECTED SUBJECT
        </div>
        <div className="flex items-center gap-2">
          {entity.confidence_level && (
            <span
              style={{ color: cm.color, borderColor: `${cm.color}60` }}
              className="font-mono text-[8px] uppercase tracking-[0.25em] border px-1.5 py-[1px]"
              data-testid="ef-inline-confidence"
            >
              {cm.label}
            </span>
          )}
          {onClear && (
            <button
              type="button"
              onClick={onClear}
              data-testid="ef-inline-clear"
              title="Clear selection (Esc)"
              className="border border-[#2D2A26] hover:border-[#D92D20] text-[#A39E93] hover:text-[#D92D20] px-1.5 py-0.5 font-mono text-[8px] uppercase tracking-[0.25em] flex items-center gap-1"
            >
              <X size={9} /> Esc
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Image / silhouette */}
        <div className="w-full aspect-[4/5] max-h-[260px] border border-[#2D2A26] bg-[#0a0907] relative overflow-hidden">
          {(entity.photo_url || entity.thumbnail_url || entity.image_url) ? (
            <img
              src={entity.photo_url || entity.thumbnail_url || entity.image_url}
              alt={entity.name}
              loading="lazy"
              decoding="async"
              // Bias crop to top for non-anchor headshots; anchor uses curated
              // full-bleed framing so we keep its default center crop.
              className={
                "absolute inset-0 w-full h-full object-cover " +
                (entity.is_anchor ? "object-center" : "object-top")
              }
              data-testid="ef-inline-photo"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-[#454038]">
              <ImageOff size={42} strokeWidth={1.2} />
              <div className="font-mono text-[9px] uppercase tracking-[0.3em] mt-2 text-[#5a544a]">
                NO PHOTO ON FILE
              </div>
            </div>
          )}
          {entity.is_anchor && (
            <div className="absolute top-2 left-2 font-mono text-[8px] uppercase tracking-[0.3em] text-[#0F0F0F] bg-[#D92D20] px-1.5 py-[1px]">
              ANCHOR SUBJECT
            </div>
          )}
          <div className="absolute bottom-2 right-2 font-mono text-[8px] uppercase tracking-[0.3em] text-[#D92D20] border border-[#D92D20]/60 bg-[#0a0907]/80 px-1.5 py-[1px]">
            // FILE OPEN
          </div>
        </div>

        {/* Name + kind */}
        <div>
          <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065] mb-1">
            {meta.label || (entity.kind || "—").toUpperCase()}
          </div>
          <h2
            className="font-display text-2xl text-[#E4DFD3] leading-tight"
            data-testid="ef-inline-name"
          >
            {entity.name}
          </h2>
          {entity.occupation_or_role && (
            <div className="text-[12px] text-[#A39E93] mt-1">
              {entity.occupation_or_role}
            </div>
          )}
        </div>

        {/* Overview */}
        {(entity.summary || entity.connection_summary_to_epstein) && (
          <div>
            <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#D92D20] mb-1.5">
              // OVERVIEW
            </div>
            <p className="text-[12px] text-[#CBB892] leading-relaxed">
              {entity.summary || entity.connection_summary_to_epstein}
            </p>
          </div>
        )}

        {/* Categories / tags */}
        <div className="flex flex-wrap gap-1.5">
          {entity.category_label && (
            <span className="font-mono text-[9px] uppercase tracking-[0.25em] border border-[#454038] text-[#A39E93] px-2 py-1">
              {entity.category_label}
            </span>
          )}
          {entity.nationality && (
            <span className="font-mono text-[9px] uppercase tracking-[0.25em] border border-[#454038] text-[#A39E93] px-2 py-1">
              {entity.nationality}
            </span>
          )}
          <span className="font-mono text-[9px] uppercase tracking-[0.25em] border border-[#454038] text-[#A39E93] px-2 py-1">
            {conn} CONN
          </span>
          <span className="font-mono text-[9px] uppercase tracking-[0.25em] border border-[#454038] text-[#A39E93] px-2 py-1">
            {(entity.source_refs || []).length} SRC
          </span>
        </div>

        {/* Date window */}
        {(entity.first_known_connection_date || entity.last_known_connection_date) && (
          <div>
            <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#D92D20] mb-1.5">
              // KNOWN CONNECTION WINDOW
            </div>
            <div className="font-mono text-[11px] text-[#CBB892]">
              {entity.first_known_connection_date || "?"} → {entity.last_known_connection_date || "?"}
            </div>
          </div>
        )}

        {/* Kind-specific facts (aircraft tail number / flight route / shell jurisdiction / etc.) */}
        {(() => {
          const badges = kindMetaBadges(entity);
          if (!badges.length) return null;
          return (
            <div data-testid="ef-inline-kind-meta">
              <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#D92D20] mb-1.5">
                // RECORD DETAILS
              </div>
              <div
                className="grid gap-x-2 gap-y-1"
                style={{ gridTemplateColumns: "max-content 1fr" }}
              >
                {badges.map((b) => (
                  <React.Fragment key={b.label}>
                    <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#757065] whitespace-nowrap">
                      {b.label}
                    </span>
                    <span className="font-mono text-[11px] text-[#CBB892]">
                      {b.value}
                    </span>
                  </React.Fragment>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Legal note */}
        {entity.legal_note && (
          <div className="border-l-2 border-[#E0A526] pl-2 text-[11px] italic text-[#CBB892]">
            ⚠ {entity.legal_note}
          </div>
        )}

        {/* Sources */}
        {ownSources.length > 0 && (
          <div>
            <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#D92D20] mb-1.5">
              // SOURCES ({ownSources.length})
            </div>
            <ul className="space-y-1">
              {ownSources.slice(0, 6).map((s) => (
                <li key={s.id}>
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`ef-inline-source-${s.id}`}
                    className="group block text-[11px] text-[#A39E93] hover:text-[#E4DFD3] border-b border-[#2D2A26]/60 py-1"
                  >
                    <div className="flex items-start gap-1.5">
                      <ExternalLink
                        size={10}
                        className="mt-0.5 text-[#5a544a] group-hover:text-[#D92D20] shrink-0"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="truncate">{s.title || s.url}</div>
                        {s.domain && (
                          <div className="font-mono text-[9px] text-[#5a544a] uppercase tracking-[0.2em]">
                            {s.domain}
                          </div>
                        )}
                      </div>
                    </div>
                  </a>
                </li>
              ))}
              {ownSources.length > 6 && (
                <li className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#5a544a] pt-1">
                  + {ownSources.length - 6} more on the file page
                </li>
              )}
            </ul>
          </div>
        )}
      </div>

      {/* Footer CTA */}
      <div className="border-t border-[#2D2A26] p-3">
        <Link
          to={`/epstein-files/entity/${entity.slug}`}
          data-testid="ef-inline-view-all"
          className="flex items-center justify-center gap-2 w-full bg-[#D92D20] hover:bg-[#B92318] text-white font-mono text-[10px] uppercase tracking-[0.25em] px-3 py-2.5"
        >
          View All Connections <ArrowRight size={12} />
        </Link>
      </div>
    </aside>
  );
};

export default InlineDetailPanel;
